import type {
  OrdersBrowserPaymentFilter,
  OrdersBrowserSortKey,
  OrdersBrowserStatusFilter,
} from '@/types'
import type { ReportsSearch } from './reportsRouteSearch'

/**
 * F-RPT-N30 — the operator-facing facets of the Orders browser. Lives in the
 * route search (`?ot=&pay=&sub=&st=&sort=&dir=`) so an Overview row can link
 * straight to "FoodPanda, card, 4 Sep" and the URL is shareable.
 */
export interface OrdersBrowserFilters {
  orderTypes: string[]
  payment: OrdersBrowserPaymentFilter
  /** Online provider slug; only meaningful when payment === 'online'. */
  subMethod: string
  status: OrdersBrowserStatusFilter
  sort: OrdersBrowserSortKey | null
  dir: 'asc' | 'desc'
}

export const EMPTY_ORDERS_FILTERS: OrdersBrowserFilters = {
  orderTypes: [],
  payment: '',
  subMethod: '',
  status: '',
  sort: null,
  dir: 'desc',
}

const SORT_KEYS: readonly OrdersBrowserSortKey[] = [
  'time',
  'date',
  'total',
  'order_number',
  'table',
  'server',
  'payment',
  'order_type',
  'status',
]

export function isOrdersSortKey(v: unknown): v is OrdersBrowserSortKey {
  return typeof v === 'string' && (SORT_KEYS as readonly string[]).includes(v)
}

export function filtersFromSearch(s: ReportsSearch): OrdersBrowserFilters {
  const orderTypes = (s.ot ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
  const payment: OrdersBrowserPaymentFilter = s.pay ?? ''
  return {
    orderTypes,
    payment,
    subMethod: payment === 'online' ? (s.sub ?? '') : '',
    status: s.st ?? '',
    sort: isOrdersSortKey(s.sort) ? s.sort : null,
    dir: s.dir ?? 'desc',
  }
}

/** Route-search patch for a filter set. Empty facets map to `undefined` so
 *  they drop out of the URL instead of lingering as `?pay=`. */
export function searchFromFilters(f: OrdersBrowserFilters): Pick<ReportsSearch, 'ot' | 'pay' | 'sub' | 'st' | 'sort' | 'dir'> {
  return {
    ot: f.orderTypes.length > 0 ? f.orderTypes.join(',') : undefined,
    pay: f.payment || undefined,
    sub: f.payment === 'online' && f.subMethod ? f.subMethod : undefined,
    st: f.status || undefined,
    sort: f.sort ?? undefined,
    dir: f.sort ? f.dir : undefined,
  }
}

/** How many facets narrow the list (sort is not a filter). */
export function activeFilterCount(f: OrdersBrowserFilters): number {
  let n = 0
  if (f.orderTypes.length > 0) n += 1
  if (f.payment) n += 1
  if (f.payment === 'online' && f.subMethod) n += 1
  if (f.status) n += 1
  return n
}

export function clearFilters(f: OrdersBrowserFilters): OrdersBrowserFilters {
  // Keep the sort — clearing "what" should not reshuffle "in which order".
  return { ...EMPTY_ORDERS_FILTERS, sort: f.sort, dir: f.dir }
}
